import { getCurrentUserId } from "../../services/authService";

export const FEED_FILTER_TYPES = [
  "post",
  "news",
  "conference",
  "publication",
  "video",
  "notice",
];

export const FILTERABLE_TYPES = new Set(FEED_FILTER_TYPES);

const DASHBOARD_FILTER_STORAGE_KEY = "dashboardSelectedTypes";

// Bumped whenever a new filter type appears. A selection saved before the type
// existed cannot have opted into it, so we opt those members in on load rather
// than hiding new content behind a box they never knowingly unticked.
const FILTER_SELECTION_VERSION = 2;
const TYPES_ADDED_IN_VERSION = { 2: ["notice"] };

const applyVersionMigrations = (types, savedVersion) => {
  // An empty selection is a deliberate "clear all"; leave it alone.
  if (!types.length) return types;
  const merged = new Set(types);
  for (let v = savedVersion + 1; v <= FILTER_SELECTION_VERSION; v += 1) {
    (TYPES_ADDED_IN_VERSION[v] || []).forEach((type) => merged.add(type));
  }
  return FEED_FILTER_TYPES.filter((type) => merged.has(type));
};

export const getDashboardFilterStorageKey = () => {
  const uid = getCurrentUserId();
  return uid
    ? `${DASHBOARD_FILTER_STORAGE_KEY}:${uid}`
    : DASHBOARD_FILTER_STORAGE_KEY;
};

export const sanitizeFilterSelection = (value) => {
  if (typeof value === "string") value = [value];
  if (!Array.isArray(value)) return [...FEED_FILTER_TYPES];
  const allowed = new Set(FEED_FILTER_TYPES);
  const filtered = value
    .map((v) => (typeof v === "string" ? v.trim() : ""))
    .filter((v) => allowed.has(v));
  // Allow empty selection (e.g., user deselects the last filter).
  return filtered;
};

export const loadSavedFilterSelection = () => {
  try {
    const key = getDashboardFilterStorageKey();
    const raw =
      (typeof localStorage !== "undefined" && localStorage.getItem(key)) ||
      (typeof sessionStorage !== "undefined" && sessionStorage.getItem(key));
    if (!raw) return [...FEED_FILTER_TYPES];
    const parsed = JSON.parse(raw);
    // A bare array is the pre-versioning format, i.e. version 1.
    if (Array.isArray(parsed))
      return applyVersionMigrations(sanitizeFilterSelection(parsed), 1);
    if (parsed && Array.isArray(parsed.selectedTypes))
      return applyVersionMigrations(
        sanitizeFilterSelection(parsed.selectedTypes),
        Number(parsed.v) || 1
      );
  } catch (e) {
    console.error("Failed to load dashboard filters", e);
  }
  return [...FEED_FILTER_TYPES];
};

export const persistFilterSelection = (types) => {
  try {
    const key = getDashboardFilterStorageKey();
    const payload = JSON.stringify({
      v: FILTER_SELECTION_VERSION,
      selectedTypes: sanitizeFilterSelection(types),
    });
    if (typeof localStorage !== "undefined") localStorage.setItem(key, payload);
    if (typeof sessionStorage !== "undefined")
      sessionStorage.setItem(key, payload);
  } catch (e) {
    console.error("Failed to persist dashboard filters", e);
  }
};
